<template>
  <div
    class="owal-login-button__container"
  >
    <a
      v-if="authenticated"
      href="#"
      @click="logOut"
    >
      Log Out ({{ username }})
    </a>
    <g-signin-button
      v-else-if="provider === 'google'"
      class="owal google provider button"
      id="google"
      :params="googleSignInParams"
      @success="loginWithGoogle"
      @error="loginError"
    >
      <font-awesome-icon :icon="['fab', 'google']" /><span>Log in with Google</span>
    </g-signin-button>
    <div
      v-else-if="provider === 'microsoft'"
      id="microsoft"
      class="owal microsoft provider button"
      @click="loginWithMicrosoft"
    >
      <font-awesome-icon :icon="['fab', 'microsoft']" /><span>Log in with Office 365</span>
    </div>
    <div
      v-else-if="provider === 'auth0'"
      id="auth0"
      class="owal auth0 provider button"
      @click="loginWithAuth0"
    >
      <font-awesome-icon :icon="['fab','microsoft']" /><span>Log in with Windows Active Directory</span>
    </div>
    <div
      v-else
      class="other provider button"
    >
      Log in with Provider
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AuthenticationContext from 'adal-angular/lib/adal'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'LoginButton',
  props: {
    provider: {
      type: String,
      default: 'google',
    },
  },
  data () {
    return {
      authenticationContext: undefined,
    }
  },
  computed: {
    ...mapGetters({
      authenticated: 'isAuthenticated',
      username: 'displayName',
    }),
    googleSignInParams () {
      return {
        client_id: '1027062249723-4qpght8p42fd88an4usq4cpvfgu3t1el.apps.googleusercontent.com',
      }
    },
    microsoftSignInParams () {
      // faces application ID 15c8de17-33c8-44e0-94cd-7cac2718c06b
      // new: faces2
      // application ID 8ee7fe2c-44c0-402d-84c8-216c8a841a58
      let redirectURI = process.env.VUE_APP_OWAL_OAUTH_MS_REDIRECT_URI

      if (!redirectURI) {
        redirectURI = 'http://localhost:8080'
      }

      const params = {
        tenant: 'common', // '5e9d822a-fc78-462b-920d-391b9a20ff6d', // tenant: Azure lingo for "The owner of the organization."
        clientId: '8ee7fe2c-44c0-402d-84c8-216c8a841a58', // 'your aad application client id',
        redirect_uri: redirectURI, // 'base uri for this application',
        cacheLocation: 'localStorage',
      }

      return params
    },
  },
  methods: {
    ...mapActions(['validateJWT', 'logOut']),
    loginWithMicrosoft () {
      const config = this.microsoftSignInParams
      this.authenticationContext = new AuthenticationContext(config)

      if (this.authenticationContext.isCallback(window.location.hash) || window !== window.parent) {
        // redirect to the location specified in the url params.
        // console.log('Handling callback')
        // console.log(`URL: ${window.location.hash}`)
        this.authenticationContext.handleWindowCallback()
      } else {
        var user = this.authenticationContext.getCachedUser()
        if (user && window.parent === window && !window.opener) {
          // great, we have a user.
          // console.log(JSON.stringify(user))
          let domain = ''
          try {
            domain = user.profile.email.split('@')[1]
          } catch (error) {
            // console.log(`Couldn't parse email. Access may be limited.`)
          }
          const token = this.authenticationContext._getItem(this.authenticationContext.CONSTANTS.STORAGE.IDTOKEN)
          this.validateJWT({ token, domain }).catch(error => this.loginError({ error, msg: `Couldn't validate token with Microsoft.` }))
        } else {
          // console.log('Signing in...')
          // no user, kick off the sign in flow.
          this.authenticationContext.login()
        }
      }
    },
    loginWithGoogle (googleUser) {
      // `googleUser` is the GoogleUser object that represents the just-signed-in user.
      // See https://developers.google.com/identity/sign-in/web/reference#users
      const domain = googleUser.getHostedDomain()

      // https://developers.google.com/identity/sign-in/web/backend-auth
      // "Warning: Do not accept plain user IDs, such as those you can get with the GoogleUser.getId() method, on your backend server."
      const token = googleUser.getAuthResponse().id_token
      this.validateJWT({ token, domain }).catch(error => this.loginError({ error, msg: `Couldn't validate token with Google.` }))
    },
    loginWithAuth0 () {
      // alt:
      // this.$auth.loginWithRedirect()
      this.$auth.loginWithPopup().then(async () => {
        if (this.$auth.isAuthenticated) {
          console.log('Logged in!')
          // get extra permissions! Though possibly not necessary
          try {
            await this.$auth.getTokenSilently({
              scope: 'openid email profile',
              audience: '',
            })
            const claims = await this.$auth.getIdTokenClaims()
            const token = claims.__raw
            this.validateJWT({ token, domain: 'unused' })
              .catch(error => this.loginError({ error, msg: `Couldn't validate token with Auth0.` }))
          } catch (e) {
            console.error('Exception while trying to do popup and validate JWT...')
            console.error(e)
          }
        } else {
          console.log('Login unsuccessful.')
          alert('Login unsuccessful.')
        }
      })
    },
    loginError (reason) {
      console.log('Auth failed')
      console.log(reason)
      let route = this.$route
      this.$router.push({
        path: route.path,
        query: {
          message: reason.msg,
        },
      })
      console.log(`OAuth login failed. Here's why: ${reason.error}`)
    },
  },
  components: {
    FontAwesomeIcon,
  },
}
</script>

<style scoped>
a {
  color: #fff;
}

g-signin-button
{
  background-color: #ffffff;
}

g-signin-button:hover
{
  background-color: rgba(0, 0, 0, 0.05);
}

.owal.provider.button {
  text-align: center;
  width: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 0.2857142857rem;
  padding: 0.2857142857rem 1rem;
  margin: 0.2857142857rem 0;
  height: 3.14rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.owal.provider.button span {
  margin: 0 0 0 0.5rem;
  color: #404040;
}

.owal.google.provider.button
{
  color: #dd4b39 !important;
}

.owal.microsoft.provider.button {
  color: #666666;
}

</style>
